// ================================================================================
// FILE: GxViewRolePermissions.tsx
// ================================================================================

import { type FC, type ReactNode, useState } from "react";
import type { Module } from "@/app/types/adminMenu";

const ICON_BASE_PATH = "/assets/icons/";

export interface GxViewRolePermissionsProps {
  roleName: string;
  description?: ReactNode;
  selfAuth?: boolean;
  modules: Module[]; // always provided by parent — dummy or real
  disabled?: boolean;
}

// ---- Permission display config ----
// Only 3 columns shown. "Add" from API is displayed as "Create".
// Fixed column order — always View, Create, Approve
const VISIBLE_COLUMNS = ["View", "Add", "Approve"] as const;
const COLUMN_LABELS: Record<string, string> = {
  View: "View",
  Add: "Create",
  Approve: "Approve",
};

// ---- Accordion row (internal) ----
// allPermNames passed from parent so columns align with the shared header

const ModuleAccordion: FC<{ module: Module }> = ({ module }) => {
  const [open, setOpen] = useState(true); // expanded by default

  return (
    <div className="  mb-2 overflow-hidden">
      {/* Accordion header */}
      <button
        className="w-full rounded-lg flex items-center justify-between px-4 py-3 border  border-[#E8E8E8] bg-[#EEEEEE80] hover:bg-[#EEF0FB] transition text-left"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="gx-p14-sb text-[#222222]">{module.moduleName}</span>
        <img
          src={`${ICON_BASE_PATH}down_arrow1.svg`}
          width={14}
          height={14}
          alt={open ? "Collapse" : "Expand"}
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        />
      </button>

      {/* Accordion body — rows only, no thead here */}
      {open && (
        <table className="w-full border-collapse gx-p14-md">
          <tbody>
            {module.menus.map((menu) => {
              // keyed by API permissionName (e.g. "Add", "View", "Approve")
              const permMap = Object.fromEntries(
                menu.permissions.map((p) => [p.permissionName, p.checkBox]),
              );
              return (
                <tr key={menu.menuId} className="border-b border-[#F2F2F2]">
                  <td className="py-4 px-4 gx-p14-sb">{menu.menuName}</td>
                  <div className="flex justify-end">
                    <div className="flex w-[10vw] justify-between">
                      {VISIBLE_COLUMNS.map((apiKey) => (
                        <td
                          key={apiKey}
                          className="py-4 flex justify-center w-[54px]"
                        >
                          {permMap[apiKey] === "Y" ? (
                            <img
                              src={`${ICON_BASE_PATH}tick.svg`}
                              width={12}
                              height={12}
                              alt="Yes"
                            />
                          ) : (
                            <span className="text-[#22222280]">—</span>
                          )}
                        </td>
                      ))}
                    </div>
                  </div>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ---- Main component ----

const GxViewRolePermissions: FC<GxViewRolePermissionsProps> = ({
  roleName,
  description,
  selfAuth = false,
  modules,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Trigger */}
      <div
        className={`gx-admin-view-permisson ${
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        }`}
        onClick={() => {
          if (!disabled) setOpen(true);
        }}
      >
        <div className="flex items-center gap-2">
          <img
            src={`${ICON_BASE_PATH}eyeopenblue.svg`}
            width={14}
            height={14}
            alt="View Role Permissions"
          />
          <p className="gx-p12-sb">View Role Permissions</p>
        </div>
      </div>

      {/* Modal */}
      {open && (
        <div className="gx-request">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl">
            {/* Modal header */}
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Role Permissions : "{roleName}"
            </h2>
            {description && (
              <p className="text-sm text-gray-600 mb-4">{description}</p>
            )}
            <p className="gx-p12-b mb-4">
              Self Auth : {selfAuth ? "Yes" : "No"}
            </p>

            {/* Shared column header — fixed 3 columns: View, Create, Approve */}
            <table className="w-full border-collapse">
              <thead>
                <tr className="text-left flex justify-between">
                  <th className="py-2 gx-p12-b text-[#172477]">Feature</th>
                  <div className="flex w-[10vw] justify-between">
                    {VISIBLE_COLUMNS.map((apiKey) => (
                      <th
                        key={apiKey}
                        className="py-2 w-[54px] gx-p12-b text-[#22222280] text-center"
                      >
                        {COLUMN_LABELS[apiKey]}
                      </th>
                    ))}
                  </div>
                </tr>
              </thead>
            </table>

            {/* Accordion list */}
            <div className="gx-scroll-section h-[25vh]">
              {modules.map((mod) => (
                <ModuleAccordion key={mod.moduleId} module={mod} />
              ))}
            </div>

            {/* Footer */}
            <div className="flex justify-end p-0!">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 rounded-md w-full border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GxViewRolePermissions;
